/**
 * PLATE CARD COMPONENT - Matching PolishCard style!
 * 
 * Features:
 * - Interactive emoji toggles at the bottom
 * - Usage meter bar showing how many times plate has been used (hidden for wishlist items)
 * - Collection/Event info display with click-to-filter
 * - Tags display with click-to-filter
 * - Selection mode support for bulk actions
 * - Click to open detail modal
 * - Grayed-out appearance when destashed
 * 
 * Note: Destash and Swap toggles moved to Detail Modal for better UX
 */

import React, { useState, useEffect } from 'react';
import { AppIcons } from '../../constants/icons';
import { CategoryColors, PriorityColors, type PriorityKey } from '../../constants/categoryColors';
import type { CollectionItem } from '../../types';
import DestashStatusBadge from '../DestashStatusBadge';
import ReleaseInfoChips from '../common/ReleaseInfoChips';
import PrivateLockBadge from '../common/PrivateLockBadge';

interface PlateCardProps {
  plate: CollectionItem;
  onUpdate: (id: string, updates: Partial<CollectionItem>) => Promise<void>;
  onClick?: () => void;
  userId: string;
  isSelectionMode?: boolean;
  isSelected?: boolean;
  onSelect?: (plateId: string) => void;
  onFilterByTag?: (tagCategory: string, tagValue: string) => void;
  onFilterByCollection?: (collectionName: string) => void;
  onFilterByEvent?: (eventSource: string) => void;
}

const PlateCard: React.FC<PlateCardProps> = ({
  plate,
  onUpdate,
  onClick,
  userId,
  isSelectionMode = false,
  isSelected = false,
  onSelect,
  onFilterByTag,
  onFilterByCollection,
  onFilterByEvent
}) => {

  // Local state for toggles - enables instant visual feedback
  const [onWishlist, setOnWishlist] = useState(plate.onWishlist || false);
  const [isFavorite, setIsFavorite] = useState(plate.isFavorite || false);
  const [isRareLE, setIsRareLE] = useState(plate.isRareLE || false);
  const [needsInfo, setNeedsInfo] = useState(plate.needsInfo || false);

  // Sync local state when props change (e.g., after modal updates)
  useEffect(() => {
    setOnWishlist(plate.onWishlist || false);
    setIsFavorite(plate.isFavorite || false);
    setIsRareLE(plate.isRareLE || false);
    setNeedsInfo(plate.needsInfo || false);
  }, [plate.onWishlist, plate.isFavorite, plate.isRareLE, plate.needsInfo]);

  // Handle emoji toggle clicks - with optimistic update!
  const handleToggle = async (field: 'onWishlist' | 'needsInfo' | 'isFavorite' | 'isRareLE', e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (isSelectionMode) return; // Disabled in selection mode

    // Get current value from local state
    let currentValue = false;
    switch (field) {
      case 'onWishlist': currentValue = onWishlist; break;
      case 'isFavorite': currentValue = isFavorite; break;
      case 'isRareLE': currentValue = isRareLE; break;
      case 'needsInfo': currentValue = needsInfo; break;
    }
    const newValue = !currentValue;

    // Update local state immediately (instant visual feedback!)
    switch (field) {
      case 'onWishlist': setOnWishlist(newValue); break;
      case 'isFavorite': setIsFavorite(newValue); break;
      case 'isRareLE': setIsRareLE(newValue); break;
      case 'needsInfo': setNeedsInfo(newValue); break;
    }

    // Save to database - when adding to wishlist, also set default ISO priority
    if (field === 'onWishlist' && newValue) {
      await onUpdate(plate.id, { [field]: newValue, isoPriority: plate.isoPriority || 'iso' });
    } else {
      await onUpdate(plate.id, { [field]: newValue });
    }
  };

  // Handle card click
  const handleCardClick = () => {
    if (isSelectionMode && onSelect) {
      onSelect(plate.id);
    } else if (onClick) {
      onClick();
    }
  };

  // Handle tag click
  const handleTagClick = (category: string, value: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onFilterByTag) {
      onFilterByTag(category, value);
    }
  };

  // Helper to check if a photo is hidden due to DMCA
  const isPhotoHidden = (photo: any): boolean => {
    return typeof photo === 'object' && photo.dmcaStatus === 'hidden';
  };

  // Get first available image - NEW PHOTO SYSTEM (filters DMCA-hidden photos)
  const getDisplayImage = (): string | null => {
    // New system: photos[] array with isDefault flag
    if (plate.photos && plate.photos.length > 0) {
      const defaultPhoto = plate.photos.find(p => p.isDefault && !isPhotoHidden(p));
      if (defaultPhoto) return defaultPhoto.url;
      const visiblePhoto = plate.photos.find(p => !isPhotoHidden(p));
      if (visiblePhoto) return visiblePhoto.url;
    }
    
    // Legacy fallback for plates
    if (plate.photoUrls && plate.photoUrls.length > 0) {
      const visible = (plate.photoUrls as any[]).find((p: any) => !isPhotoHidden(p));
      if (visible) return typeof visible === 'string' ? visible : visible.url;
    }
    if (plate.swatchPhotos && plate.swatchPhotos.length > 0) {
      const visible = (plate.swatchPhotos as any[]).find((p: any) => !isPhotoHidden(p));
      if (visible) return typeof visible === 'string' ? visible : visible.url;
    }
    if (plate.inUsePhotos && plate.inUsePhotos.length > 0) {
      const visible = (plate.inUsePhotos as any[]).find((p: any) => !isPhotoHidden(p));
      if (visible) return typeof visible === 'string' ? visible : visible.url;
    }
    
    return null;
  };
  
  const firstImage = getDisplayImage();

  // Calculate usage level
  const timesUsed = plate.timesUsed || 0;
  
  // Usage bar config
  let usageLevel = 'Untried';
  let usagePercentage = 0;
  let usageColor = 'bg-gray-300';
  let usageBarColor = 'bg-gray-400';
  
  if (isFavorite || timesUsed >= 7) {
    usageLevel = 'Fave';
    usagePercentage = 100;
    usageColor = 'bg-yellow-100';
    usageBarColor = 'bg-gradient-to-r from-yellow-400 to-orange-400';
  } else if (timesUsed >= 3) {
    usageLevel = 'Loved';
    usagePercentage = 60;
    usageColor = 'bg-pink-100';
    usageBarColor = 'bg-gradient-to-r from-pink-400 to-fuchsia-400';
  } else if (timesUsed >= 1) {
    usageLevel = 'Tried';
    usagePercentage = 20;
    usageColor = 'bg-purple-100';
    usageBarColor = 'bg-gradient-to-r from-purple-400 to-purple-500';
  }

  // Collect displayable tags from imageTags (plate-specific)
  const displayTags: { category: string; value: string; label: string }[] = [];
  
  if (plate.imageTags && plate.imageTags.length > 0) {
    plate.imageTags.forEach((tag: string) => {
      displayTags.push({ category: 'imageTags', value: tag, label: tag });
    });
  }
  
  // Also include generic tags if present
  if (plate.tags && plate.tags.length > 0) {
    plate.tags.forEach((tag: string) => {
      displayTags.push({ category: 'tags', value: tag, label: tag });
    });
  }

  // Get tag color based on category
  const getTagColor = (category: string): string => {
    switch (category) {
      case 'imageTags':
        return 'bg-indigo-600/80 hover:bg-indigo-600';
      case 'tags':
        return 'bg-purple-600/80 hover:bg-purple-600';
      default:
        return 'bg-slate-600/80 hover:bg-slate-600';
    }
  };

  return (
    <div 
      onClick={handleCardClick}
      className={`card-light overflow-hidden hover:shadow-2xl transform hover:-translate-y-1 transition-all cursor-pointer flex flex-col ${
        isSelectionMode && isSelected ? 'ring-4 ring-blue-500' : ''
      } ${plate.isDestashed ? 'opacity-100 grayscale' : ''}`}
    >
      {/* Photo with Selection Overlay */}
      <div className="relative">
        {firstImage ? (
          <img
            src={firstImage}
            alt={`${plate.brand} ${plate.name}`}
            className="w-full h-64 object-cover"
          />
        ) : (
          <div className="w-full h-64 bg-gradient-to-br from-indigo-400 to-purple-600 flex items-center justify-center">
            <AppIcons.Plate className="w-16 h-16 text-white" aria-label="Stamping Plate" />
          </div>
        )}

        {/* Selection Overlay & Checkbox */}
        {isSelectionMode && (
          <>
            {isSelected && (
              <div className="absolute inset-0 bg-blue-500 bg-opacity-30 flex items-center justify-center">
                <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            )}
            <div className="absolute top-2 left-2">
              <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                isSelected ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-400'
              }`}>
                {isSelected && (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
          </>
        )}

        {/* Plate Type Badge */}
        <div className="absolute top-2 right-2">
          <span className={`px-2 py-1 ${CategoryColors.plate.badge} rounded-full text-xs font-bold font-body`}>
            {plate.plateType || 'Stamping Plate'}
          </span>
        </div>

        {/* ISO Priority Badge - Shows when on wishlist */}
        {!isSelectionMode && plate.onWishlist && (
          <div className="absolute top-2 left-2">
            <span className={`px-2 py-1 rounded-full text-xs font-bold text-white font-body ${
              PriorityColors[plate.isoPriority as PriorityKey]?.bg || PriorityColors.iso.bg
            }`}>
              {PriorityColors[plate.isoPriority as PriorityKey]?.label || PriorityColors.iso.label}
            </span>
          </div>
        )}

        {/* Private lock — owner-only marker; suppressed in selection mode
            so it doesn't fight the selection checkbox in the top-left. */}
        {!isSelectionMode && plate.isPrivate && <PrivateLockBadge />}
      </div>
      
      {/* Info - Using flex-1 to push bottom content down */}
      <div className="p-4 flex flex-col flex-1">
        {/* Top Section - Brand & Name */}
        <div className="mb-2">
          <h3 className={`text-xs uppercase ${CategoryColors.plate.text} font-bold tracking-wide mb-1 font-heading`}>
            {plate.brand}
          </h3>
          
          <h4 className="text-lg font-bold text-light-primary leading-tight font-body">
            {plate.name}
          </h4>
        </div>

        {/* Release info chips (release type, collection, event, season/year) */}
        <ReleaseInfoChips
          item={plate as any}
          onFilterByCollection={onFilterByCollection}
          onFilterByEvent={onFilterByEvent}
        />

        {/* Tags - Clickable */}
        {displayTags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {displayTags.map((tag, index) => (
              <button
                key={`${tag.category}-${tag.value}-${index}`}
                onClick={(e) => handleTagClick(tag.category, tag.value, e)}
                className={`px-2 py-1 ${getTagColor(tag.category)} text-white rounded text-xs font-bold transition-default font-body ${
                  onFilterByTag ? 'cursor-pointer' : 'cursor-default'
                }`}
                title={`Filter by: ${tag.label}`}
              >
                {tag.label}
              </button>
            ))}
          </div>
        )}

        {/* Barcode/SKU */}
        {plate.barcode && (
          <div className="mb-2">
            <span className="text-xs text-glass-muted font-mono">{plate.barcode}</span>
          </div>
        )}

        {/* Quantity Badge */}
        <div className="mb-2">
          <span className="inline-block px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs font-medium font-body">
            Qty: {plate.quantity || 1}
          </span>
        </div>

        {/* Destash Status Badge */}
        {plate.isForDestash && (
        <div className="mb-2">
          <DestashStatusBadge
          itemId={plate.id}
          userId={userId}
          currentStatus={plate.destashStatus || 'available'}
          isForDestash={plate.isForDestash}
          quantity={plate.quantity || 1}
          destashQuantityAvailable={plate.destashQuantityAvailable}
          />
        </div>
        )}

        {/* Spacer to push bottom content down */}
        <div className="flex-1"></div>

        {/* USAGE BAR - Above emojis - ONLY show if NOT on wishlist */}
        {!onWishlist && (
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-light-secondary font-body">
                {usageLevel}
              </span>
              <span className="text-xs font-bold text-light-secondary font-body flex items-center gap-1">
                {timesUsed}x {isFavorite && <AppIcons.Favorite className="w-3 h-3 text-yellow-500 fill-yellow-500" aria-label="Favorite" />}
              </span>
            </div>
            <div className={`w-full h-2 rounded-full ${usageColor} overflow-hidden`}>
              <div 
                className={`h-full ${usageBarColor} transition-slow rounded-full`}
                style={{ width: `${usagePercentage}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* Horizontal Line */}
        <div className="border-t border border-gray-300 mb-3"></div>

        {/* ICON TOGGLES - At bottom, centered - 4 TOGGLES */}
        <div className="flex items-center justify-center gap-4">
          {/* PublicWishlist */}
          <button
            onClick={(e) => handleToggle('onWishlist', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${
              isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''
            } ${
              onWishlist ? 'text-amber-500' : 'text-light-muted opacity-40'
            }`}
            title="PublicWishlist"
            aria-label="PublicWishlist"
          >
            <AppIcons.Wishlist className={`w-6 h-6 text-current ${onWishlist ? 'fill-amber-500 text-amber-500' : ''}`} />
          </button>

          {/* Favorite */}
          <button
            onClick={(e) => handleToggle('isFavorite', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${
              isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''
            } ${
              isFavorite ? 'text-pink-500' : 'text-light-muted opacity-40'
            }`}
            title="Favorite"
            aria-label="Favorite"
          >
            <AppIcons.Favorite className={`w-6 h-6 text-current ${isFavorite ? 'fill-pink-500' : ''}`} />
          </button>

          {/* Rare/LE */}
          <button
            onClick={(e) => handleToggle('isRareLE', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${
              isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''
            } ${
              isRareLE ? 'text-purple-500' : 'text-light-muted opacity-40'
            }`}
            title="Rare/Limited Edition"
            aria-label="Rare/Limited Edition"
          >
            <AppIcons.RareLE className={`w-6 h-6 ${isRareLE ? 'fill-purple-500' : ''}`} />
          </button>

          {/* Needs Info */}
          <button
            onClick={(e) => handleToggle('needsInfo', e)}
            disabled={isSelectionMode}
            className={`transition-all transform hover:scale-125 ${
              isSelectionMode ? 'opacity-40 cursor-not-allowed' : ''
            } ${
              needsInfo ? 'text-blue-500' : 'text-light-muted opacity-40'
            }`}
            title="Needs Info"
            aria-label="Needs Info"
          >
            <AppIcons.NeedsInfo className={`w-6 h-6 text-current ${needsInfo ? 'fill-blue-500' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlateCard;
